import {
  SearchFilterCondition,
  SearchFilterField,
  SearchFilters,
} from 'lib-search-types';
import { convertChannelCasing } from 'lib-shared-api-utils';
import memoizeOne from 'memoize-one';
import moment from 'moment-timezone';

import { channelAttributes } from 'utils/channels';

import {
  dateRangePresets,
  filterItems,
  filtersInitialState,
} from '../../components/filters/utils/definitions';
import { convertMsToDuration } from '../duration/convertDuration';

import {
  isCustomAttributeCondition,
  isDateFilterField,
  isLeafFilterCondition,
} from './filterTypeGuards';

/** Returns the display `label` for a static filter `value`. */
const getStaticFilterLabel = memoizeOne(
  (field: SearchFilterField<'conversations'>, value: string) => {
    switch (field) {
      case 'status':
      case 'initial_direction':
        return filterItems[field]?.find(({ id }) => id === value)?.name;

      case 'initial_channel':
        return channelAttributes[convertChannelCasing(value, 'snake')]?.name;

      case 'duration': {
        const duration = convertMsToDuration(parseInt(value, 10));
        const unit =
          duration.value === 1 ? duration.unit.slice(0, -1) : duration.unit;
        return `${duration.value} ${unit.toLowerCase()}`;
      }

      case 'numeric_id':
        return `#${value}`;

      case 'note_message':
        return value;

      default: {
        if (isDateFilterField(field)) {
          const preset = dateRangePresets.find(({ key }) => key === value);

          if (preset) {
            return preset.label;
          }

          const isDateFormat = moment(value, 'YYYY-MM-DD', true).isValid();
          return moment(value).format(isDateFormat ? 'L' : 'L LT');
        }
        return '';
      }
    }
  },
);

/**
 * Adds missing labels to the list of search filter conditions. Only handles
 * static filters.
 */
export const enrichStaticFilters = memoizeOne(
  (
    filters?: SearchFilters<'conversations'>,
  ): SearchFilters<'conversations'> => {
    if (!filters || !filters.filters.length) return filtersInitialState;

    const conditions: SearchFilterCondition<'conversations'>[] = filters.filters
      .filter(isLeafFilterCondition)
      .map((condition) => {
        if (isCustomAttributeCondition(condition)) {
          return condition;
        }

        const { field, operator, values, ...rest } = condition;

        /** @ts-expect-error [INSIGHTS-832] Search migration */
        const enrichedValues = values.map(({ label: origLabel, value }) => {
          const label =
            origLabel || getStaticFilterLabel(field, value as string) || '';
          return { label, value };
        });

        return {
          field,
          operator,
          values: enrichedValues,
          ...rest,
        } as SearchFilterCondition<'conversations'>;
      });

    return { strategy: filters.strategy, filters: conditions };
  },
);
